import { InputBase } from '@mui/material';
import Fuse from 'fuse.js';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useClientsQuery, useSearchClientsQuery } from '@features/client/queries';
import { setSearchResults } from '../../libs/redux/searchResultsReducer';
import { useAppDispatch, useAppSelector } from '../../libs/redux/store';
import { useIntl } from 'react-intl';
import { setSearchState } from '../../libs/redux/appUiSlice';
import { queryClient } from '@shared/queries';
import { useDebounce } from '@shared/store';

const ClientSearch = () => {
  const { data: clients } = useClientsQuery();
  const [query, setQuery] = useState('');
  const intl = useIntl();
  const debouncedQuery = useDebounce(query, 200);
  const dispatch = useAppDispatch();
  const prevResultsRef = useRef<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isSearchActive = useAppSelector(state => state.appUi.isSearchActive);

  useEffect(() => {
    if (isSearchActive) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isSearchActive]);
  const fuse = useMemo(
    () =>
      new Fuse(clients ?? [], {
        keys: [
          { name: 'lastName', weight: 0.1 },
          { name: 'firstName', weight: 0.2 },
          { name: 'phone', weight: 0.3 },
        ],
        threshold: 0.2,
        location: 0,
        distance: 40,
      }),
    [clients],
  );

  const defaultClients = useMemo(() => clients?.slice(0, 20).map(client => client.id), []);

  const results = debouncedQuery
    ? fuse.search(debouncedQuery).map(r => r.item.id)
    : (defaultClients ?? []);

  const resultsKey = JSON.stringify(results);
  const { data: clientsWithVisits } = useSearchClientsQuery(results, !!results.length);

  useEffect(() => {
    if (resultsKey !== prevResultsRef.current) {
      prevResultsRef.current = resultsKey;
      queryClient.invalidateQueries({ queryKey: ['searchClients'] });
    }

    if (clientsWithVisits) {
      dispatch(setSearchResults(clientsWithVisits));
    }
  }, [resultsKey, clientsWithVisits, dispatch]);

  return (
    <InputBase
      inputRef={inputRef}
      placeholder={intl.formatMessage({
        defaultMessage: 'Zadejte jméno, příjmení...',
        id: 'searchPlaceholder',
      })}
      fullWidth
      onBlur={() => setQuery('')}
      onClick={e => {
        dispatch(setSearchState(true));
        e.stopPropagation();
      }}
      value={query}
      onChange={e => setQuery(e.target.value)}
      sx={{ color: '#ffffff' }}
    />
  );
};

export default ClientSearch;
